'use strict';
/* pk-lander.js — shared behaviour for the Pumpkin lander sections.
   Null-safe: every module skips itself if its section is absent, so sections
   can be added/removed/reordered without breaking the page. Adapted from the
   original pumpkin-preview.js. */

/* ---- Offer selector + gallery + before/after + FAQ + dialogs + sticky ---- */
(() => {
  const $  = s => document.querySelector(s);
  const $$ = s => [...document.querySelectorAll(s)];
  const setAll = (selector, value) => $$(selector).forEach(el => el.textContent = value);

  /* Offer selector (buy box) --------------------------------------------- */
  const offerEl = $('#offer-data');
  if (offerEl) {
    let offers = [];
    try { offers = JSON.parse(offerEl.textContent); } catch (e) { offers = []; }
    if (offers.length) {
      let selected = offers.find(o => o.default) || offers[0];
      const money = v => '$' + (v / 100).toFixed(2);
      const discount = o => Math.floor((o.regular - o.price) / o.regular * 100);
      const bottles = q => q + ' ' + (q === 1 ? 'bottle' : 'bottles');
      const ctaFor = o => o.cta || ('Get my ' + bottles(o.quantity));
      function updateOffer(id) {
        selected = offers.find(o => o.id === id) || selected;
        const label = bottles(selected.quantity) + ' · ' + selected.days + '-day supply';
        const per = selected.perBottle || 60;
        setAll('[data-selected-label]', label + ' · One-time purchase');
        setAll('[data-sticky-label]', label);
        setAll('[data-selected-price]', money(selected.price));
        setAll('[data-selected-saving]', 'You save ' + money(selected.regular - selected.price) + ' today');
        setAll('[data-selected-regular]', money(selected.regular));
        setAll('[data-selected-discount]', discount(selected) + '% OFF');
        setAll('[data-selected-cta]', ctaFor(selected));
        setAll('[data-selected-shipping]', selected.quantity > 1 ? 'Free shipping' : 'Shipping at checkout');
        setAll('[data-cart-shipping]', selected.quantity > 1 ? 'Free shipping' : 'Calculated at checkout');
        setAll('[data-cart-contents]', bottles(selected.quantity) + ' · ' + (selected.quantity * per) + ' softgels · ' + selected.days + '-day supply');
      }
      $$('input[name="supply"]').forEach(i => i.addEventListener('change', () => updateOffer(i.value)));
      updateOffer(selected.id);

      // Scroll to the "Your selected supply" summary when a bundle is chosen.
      const summary = document.querySelector('.checkout-summary');
      if (summary) {
        const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
        const scrollToSummary = () => summary.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
        // Click covers re-clicking the already-selected card; change covers keyboard selection.
        $$('.offer-card').forEach(card => card.addEventListener('click', () => setTimeout(scrollToSummary, 50)));
        $$('input[name="supply"]').forEach(i => i.addEventListener('change', () => setTimeout(scrollToSummary, 50)));
      }
    }
  }

  /* Gallery (hero) ------------------------------------------------------- */
  const main = $('#gallery-main');
  const gallery = $('.gallery');
  const stage = $('#gallery-stage');
  if (main && $$('.thumb').length) {
    const thumbs = $$('.thumb');
    let slide = 0, swipeTime = 0;
    function fullSrc(i) {
      const t = thumbs[i];
      if (t.dataset.full) return t.dataset.full;
      if (window.NURALIS_GALLERY && window.NURALIS_GALLERY[i]) return window.NURALIS_GALLERY[i];
      const img = t.querySelector('img');
      return img ? img.src.replace('-thumb', '') : main.src;
    }
    function showSlide(next) {
      slide = (next + thumbs.length) % thumbs.length;
      main.src = fullSrc(slide);
      main.alt = thumbs[slide].dataset.alt || main.alt;
      thumbs.forEach((b, i) => b.setAttribute('aria-pressed', String(i === slide)));
    }
    thumbs.forEach(b => b.addEventListener('click', () => showSlide(Number(b.dataset.slide))));
    const prev = $('#gallery-prev'), next = $('#gallery-next');
    if (prev) prev.addEventListener('click', () => showSlide(slide - 1));
    if (next) next.addEventListener('click', () => showSlide(slide + 1));
    if (gallery) gallery.addEventListener('keydown', e => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); showSlide(slide + (e.key === 'ArrowRight' ? 1 : -1)); }
    });
    if (stage) {
      let touch = null;
      stage.addEventListener('touchstart', e => { touch = [e.changedTouches[0].clientX, e.changedTouches[0].clientY]; }, { passive: true });
      stage.addEventListener('touchend', e => {
        if (!touch) return;
        const dx = e.changedTouches[0].clientX - touch[0], dy = e.changedTouches[0].clientY - touch[1];
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) { showSlide(slide + (dx < 0 ? 1 : -1)); swipeTime = Date.now(); }
        touch = null;
      }, { passive: true });
    }
    // expose swipeTime for zoom guard below
    window.__pkSwipeTime = () => swipeTime;
  }

  /* Before / after reveal (results) -------------------------------------- */
  const reveal = $('#ba-range'), ba = $('#before-after');
  if (reveal && ba) {
    function setReveal(v) {
      const n = Math.max(0, Math.min(100, Number(v)));
      reveal.value = n;
      ba.style.setProperty('--reveal', n + '%');
      reveal.setAttribute('aria-valuetext', n + ' percent before, ' + (100 - n) + ' percent after');
      $$('[data-reveal]').forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.reveal) === n)));
    }
    reveal.addEventListener('input', () => setReveal(reveal.value));
    $$('[data-reveal]').forEach(b => b.addEventListener('click', () => setReveal(b.dataset.reveal)));
  }

  /* FAQ single-open accordion ------------------------------------------- */
  const faqs = $$('.faqs details');
  faqs.forEach(d => d.addEventListener('toggle', () => {
    if (d.open) faqs.forEach(o => { if (o !== d) o.open = false; });
  }));

  /* Dialogs (zoom + cart) ----------------------------------------------- */
  function openDialog(d) { if (d && d.showModal) { d.showModal(); document.body.classList.add('modal-open'); } }
  $$('dialog').forEach(d => {
    d.addEventListener('close', () => document.body.classList.remove('modal-open'));
    d.addEventListener('click', e => {
      if (e.target === d) { const r = d.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) d.close(); }
    });
  });
  $$('[data-close]').forEach(b => b.addEventListener('click', () => { const t = $('#' + b.dataset.close); if (t) t.close(); }));
  const addSupply = $('#add-supply'), cartDialog = $('#cart-dialog');
  if (addSupply) {
    // Ascendify hooks. Change only if the theme uses different names.
    const DRAWER_SELECTOR = 'cart-drawer';
    const BUBBLE_ID = 'cart-icon-bubble';
    const SECTIONS = 'cart-drawer,cart-icon-bubble';
    const selectedVariant = () => { const r = $$('input[name="supply"]').find(x => x.checked) || $('input[name="supply"]'); return r ? r.dataset.variant : ''; };
    const mode = addSupply.dataset.cartMode || 'cart';
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    // Update the <cart-drawer> in place: keep the node (so header toggle + open() survive),
    // but copy the freshly-rendered element's attributes (its class carries the non-empty state,
    // e.g. removes "is-empty") and its inner html. Never blanks.
    const swapDrawer = (destDrawer, html) => {
      if (!destDrawer || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(DRAWER_SELECTOR) || doc.getElementById('shopify-section-cart-drawer') || doc.querySelector('.shopify-section') || doc.body;
      if (!fresh) return;
      const inner = fresh.innerHTML;
      if (!inner || !inner.trim()) return; // guard: never blank the drawer
      if (fresh.tagName && fresh.tagName.toLowerCase() === DRAWER_SELECTOR) {
        // matched the real element — mirror its state-bearing attributes (class, data-*)
        [...fresh.attributes].forEach(a => { try { destDrawer.setAttribute(a.name, a.value); } catch (e) {} });
      } else {
        // matched a wrapper — clear common empty-state classes so content isn't hidden by CSS
        destDrawer.classList.remove('is-empty', 'empty', 'cart-empty', 'drawer--empty', 'cart--empty');
      }
      destDrawer.innerHTML = inner;
    };
    // Update the cart count bubble in place (keeps the header link + its listener).
    const swapBubble = (html) => {
      const dest = document.getElementById(BUBBLE_ID);
      if (!dest || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById(BUBBLE_ID) || doc.querySelector('.shopify-section') || doc.body;
      const inner = fresh ? fresh.innerHTML : '';
      if (inner && inner.trim()) dest.innerHTML = inner;
    };

    addSupply.addEventListener('click', async () => {
      const variant = selectedVariant();
      const drawer = document.querySelector(DRAWER_SELECTOR);
      // Preview fallback: preview mode, OR no variant wired, OR no cart drawer present (e.g. the standalone preview theme).
      if (mode === 'preview' || !variant || !drawer) { if (cartDialog) openDialog(cartDialog); return; }
      addSupply.setAttribute('aria-busy', 'true');
      try {
        // 1) Add the item (plain add.js — no theme JS API involved).
        const add = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variant), quantity: 1 }] })
        });
        if (!add.ok) throw new Error('add failed');
        // 2) Pull fresh drawer + count markup via the Section Rendering API.
        const res = await fetch(root + '?sections=' + encodeURIComponent(SECTIONS));
        const data = await res.json();
        // 3) Update both in place.
        swapDrawer(drawer, data['cart-drawer']);
        swapBubble(data['cart-icon-bubble']);
        // 4) Open the drawer via its own method.
        if (typeof drawer.open === 'function') drawer.open();
        else drawer.classList.add('active', 'is-open', 'open');
      } catch (e) { window.location = root + 'cart'; }
      finally { addSupply.removeAttribute('aria-busy'); }
    });
  }
  const zoomTrigger = $('#zoom-trigger'), zoomDialog = $('#zoom-dialog'), zoomImg = $('#zoom-image');
  if (zoomTrigger && zoomDialog && main) zoomTrigger.addEventListener('click', () => {
    if (window.__pkSwipeTime && Date.now() - window.__pkSwipeTime() < 350) return;
    if (zoomImg) { zoomImg.src = main.src; zoomImg.alt = main.alt; }
    openDialog(zoomDialog);
  });

  /* Sticky buy bar ------------------------------------------------------- */
  const sticky = $('#sticky-buy'), top = $('#top'), supply = $('#supply');
  if (sticky && top && supply) {
    let queued = false;
    function update() {
      queued = false;
      const heroBottom = top.getBoundingClientRect().bottom;
      const buy = supply.getBoundingClientRect();
      const visible = heroBottom < 0 && (buy.bottom < 0 || buy.top > innerHeight);
      sticky.classList.toggle('visible', visible);
      sticky.inert = !visible || innerWidth > 760;
    }
    function schedule() { if (!queued) { queued = true; requestAnimationFrame(update); } }
    addEventListener('scroll', schedule, { passive: true });
    addEventListener('resize', schedule);
    update();
  }
})();

/* ---- Comparison tabs (compare section) ------------------------------- */
(() => {
  const tabs = [...document.querySelectorAll('[data-compare]')];
  if (!tabs.length) return;
  function select(index, focus = false) {
    tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = i !== index;
    });
    if (focus) tabs[index].focus();
  }
  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => select(i));
    tab.addEventListener('keydown', e => {
      let n = i;
      if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') n = (i + tabs.length - 1) % tabs.length;
      else if (e.key === 'Home') n = 0;
      else if (e.key === 'End') n = tabs.length - 1;
      else return;
      e.preventDefault(); select(n, true);
    });
  });
})();

/* ---- Educational follicle-miniaturization canvas (science section) --- */
(() => {
  const canvas = document.getElementById('growth-canvas');
  if (!canvas) return;
  const ctx = canvas.getContext('2d'); if (!ctx) return;
  const tabs = [...document.querySelectorAll('[data-cycle]')];
  const description = document.getElementById('cycle-description');
  const toggle = document.getElementById('motion-toggle');
  if (!tabs.length || !description || !toggle) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const copy = [
    ['A larger follicle. A fuller strand.', 'Hair is produced within a living follicle. The size and activity of that follicle influence the strand it produces.'],
    ['Sensitivity can change the growth pattern.', 'In pattern thinning, sensitive follicles can gradually produce shorter, finer hairs over successive growth cycles.'],
    ['A smaller follicle. A finer strand.', 'This gradual change is called miniaturization. Not all hair loss follows this pattern, and shedding can have other causes.']
  ];
  let phase = 0, current = 0, paused = reduced.matches, visible = false, manual = false, frame = 0, last = 0, elapsed = 0, clock = 0, raf = 0;
  const W = 720, H = 580;
  function fit() { const dpr = Math.min(devicePixelRatio || 1, 2); canvas.width = W * dpr; canvas.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); draw(); }
  function pick(n, byUser = false) {
    phase = n; manual = manual || byUser; elapsed = 0;
    tabs.forEach((b, i) => { b.setAttribute('aria-selected', String(i === n)); b.tabIndex = i === n ? 0 : -1; });
    description.setAttribute('aria-labelledby', tabs[n].id);
    description.querySelector('strong').textContent = copy[n][0];
    description.querySelector('p').textContent = copy[n][1];
    canvas.dataset.phase = String(n);
    if (paused) { current = n; draw(); }
    start();
  }
  function ellipse(x, y, rx, ry, fill, stroke) { ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2); ctx.fillStyle = fill; ctx.fill(); if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 2; ctx.stroke(); } }
  function label(text, x, y, size, color = '#513524') { ctx.fillStyle = color; ctx.font = '700 ' + size + 'px Arial'; ctx.textAlign = 'center'; ctx.fillText(text, x, y); }
  function follicle(x, mini, baseline) {
    const surface = 251, depth = 219 - mini * 66, bulb = 38 - mini * 12, shaft = 21 - mini * 7, tip = 62 + mini * 39;
    const sway = Math.sin(clock * .001 + (baseline ? 0 : 1)) * 5;
    const root = surface + depth;
    const glow = ctx.createRadialGradient(x, root - 60, 15, x, root - 60, 160); glow.addColorStop(0, '#f3ae8570'); glow.addColorStop(1, '#f3ae8500'); ctx.fillStyle = glow; ctx.fillRect(x - 140, surface, 280, 300);
    ctx.beginPath(); ctx.moveTo(x - 23, surface); ctx.bezierCurveTo(x - 24, surface + 80, x - bulb - 25, root - 85, x - bulb, root - 10); ctx.bezierCurveTo(x - bulb + 7, root + 40, x + bulb - 7, root + 40, x + bulb, root - 10); ctx.bezierCurveTo(x + bulb + 25, root - 85, x + 24, surface + 80, x + 23, surface); ctx.closePath();
    const sheath = ctx.createLinearGradient(x - bulb, 0, x + bulb, 0); sheath.addColorStop(0, '#b35f43'); sheath.addColorStop(.22, '#e29a75'); sheath.addColorStop(.5, '#f2c498'); sheath.addColorStop(.78, '#da8862'); sheath.addColorStop(1, '#ac563d'); ctx.fillStyle = sheath; ctx.fill(); ctx.strokeStyle = '#a8583f'; ctx.lineWidth = 2; ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x - shaft * .52, root - 9); ctx.bezierCurveTo(x - shaft * .67, root - 60, x - shaft * .42, surface + 36, x - shaft * .43, surface); ctx.bezierCurveTo(x - shaft * .48, surface - 100, x - 50 + sway, tip + 55, x - 22 + sway, tip); ctx.bezierCurveTo(x - 37 + sway, tip + 81, x + shaft * .4, surface - 61, x + shaft * .43, surface); ctx.bezierCurveTo(x + shaft * .42, surface + 40, x + shaft * .67, root - 60, x + shaft * .52, root - 9); ctx.closePath();
    const hair = ctx.createLinearGradient(x - 20, 0, x + 20, 0); hair.addColorStop(0, '#1f1714'); hair.addColorStop(.52, '#583728'); hair.addColorStop(.67, '#a07952'); hair.addColorStop(1, '#2c1c16'); ctx.fillStyle = hair; ctx.fill();
    ellipse(x, root + 1, bulb * .37, bulb * .34, '#b9674a'); ellipse(x, root + 8, bulb * .22, bulb * .25, '#f4c491');
    for (let i = 0; i < 10; i++) { const a = i * Math.PI / 5 + clock * .00022; ellipse(x + Math.cos(a) * (bulb * .79), root - 8 + Math.sin(a) * bulb * .53, 2.4, 2.4, '#ffd8a9'); }
    ctx.strokeStyle = '#bc745b90'; ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(x, 566); ctx.bezierCurveTo(x - 13, root + 65, x + 8, root + 39, x, root + 20); ctx.moveTo(x - 42, 565); ctx.bezierCurveTo(x - 37, root + 56, x - 12, root + 50, x - 10, root + 25); ctx.moveTo(x + 45, 565); ctx.bezierCurveTo(x + 18, root + 72, x + 12, root + 37, x + 10, root + 24); ctx.stroke();
    ellipse(x, surface, 28, 6, '#98603b20');
  }
  function draw() {
    ctx.clearRect(0, 0, W, H);
    const bg = ctx.createLinearGradient(0, 0, 0, H); bg.addColorStop(0, '#fff9f1'); bg.addColorStop(1, '#f6e3cc'); ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
    label('FULLER FOLLICLE', 190, 32, 17); label('OVER GROWTH CYCLES', 525, 32, 17);
    ctx.beginPath(); ctx.moveTo(0, 251); ctx.bezierCurveTo(140, 240, 200, 265, 360, 251); ctx.bezierCurveTo(480, 240, 620, 262, 720, 251); ctx.lineTo(720, H); ctx.lineTo(0, H); ctx.closePath();
    const tissue = ctx.createLinearGradient(0, 251, 0, H); tissue.addColorStop(0, '#efbe94'); tissue.addColorStop(.18, '#e2a27e'); tissue.addColorStop(.7, '#f1c2a0'); tissue.addColorStop(1, '#f6d4b4'); ctx.fillStyle = tissue; ctx.fill();
    ctx.save(); ctx.clip(); for (let y = 300; y < 585; y += 35) { for (let x = 0; x < 740; x += 42) { const offset = (Math.floor(y / 35) % 2) * 20; ellipse(x + offset, y, 17, 10, '#ffddbf2d', '#b67b5820'); } } ctx.restore();
    ctx.beginPath(); ctx.moveTo(0, 251); ctx.bezierCurveTo(140, 240, 200, 265, 360, 251); ctx.bezierCurveTo(480, 240, 620, 262, 720, 251); ctx.strokeStyle = '#ba7750'; ctx.lineWidth = 5; ctx.stroke();
    ctx.beginPath(); ctx.moveTo(358, 61); ctx.lineTo(358, 555); ctx.setLineDash([5, 9]); ctx.strokeStyle = '#80573125'; ctx.lineWidth = 2; ctx.stroke(); ctx.setLineDash([]);
    follicle(190, 0, true); follicle(525, current, false);
    ctx.fillStyle = '#8b5a2b'; ctx.beginPath(); ctx.moveTo(338, 158); ctx.lineTo(370, 158); ctx.lineTo(360, 149); ctx.lineTo(366, 143); ctx.lineTo(386, 164); ctx.lineTo(366, 185); ctx.lineTo(360, 179); ctx.lineTo(370, 170); ctx.lineTo(338, 170); ctx.closePath(); ctx.fill();
    canvas.dataset.frame = String(++frame);
  }
  function animate(t) { raf = 0; const dt = Math.min(t - (last || t), 50); last = t; clock += dt; elapsed += dt; current += (phase - current) * Math.min(1, dt / 330); if (!manual && elapsed > 5300) { pick((phase + 1) % 3); } draw(); if (!paused && visible && !document.hidden) raf = requestAnimationFrame(animate); }
  function start() { if (!raf && !paused && visible && !document.hidden) { last = 0; raf = requestAnimationFrame(animate); } }
  function motionUI() { toggle.setAttribute('aria-pressed', String(paused)); toggle.setAttribute('aria-label', (paused ? 'Play' : 'Pause') + ' hair growth animation'); toggle.querySelector('.motion-word').textContent = paused ? 'Play' : 'Pause'; toggle.querySelector('.motion-glyph').textContent = paused ? '▶' : 'Ⅱ'; }
  tabs.forEach((b, i) => {
    b.addEventListener('click', () => pick(i, true));
    b.addEventListener('keydown', e => { let n = i; if (e.key === 'ArrowRight') n = (i + 1) % 3; else if (e.key === 'ArrowLeft') n = (i + 2) % 3; else if (e.key === 'Home') n = 0; else if (e.key === 'End') n = 2; else return; e.preventDefault(); pick(n, true); tabs[n].focus(); });
  });
  toggle.addEventListener('click', () => { paused = !paused; if (paused) { cancelAnimationFrame(raf); raf = 0; } motionUI(); start(); });
  reduced.addEventListener('change', e => { paused = e.matches; motionUI(); if (paused) { cancelAnimationFrame(raf); raf = 0; } start(); });
  new IntersectionObserver(entries => { visible = entries[0].isIntersecting; if (!visible) { cancelAnimationFrame(raf); raf = 0; } else start(); }, { threshold: .1 }).observe(canvas);
  document.addEventListener('visibilitychange', () => { if (document.hidden) { cancelAnimationFrame(raf); raf = 0; } else start(); });
  fit(); pick(0); motionUI();
})();
